import React, { useEffect } from 'react';
import _ from 'lodash';
import store, { brokerClientActions } from '@apply/adminStore';
import { Accordion, SortableHeader, InformModal } from '../../common/SharedComponents';
import { BrokerClientApi, FileApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { useSelector } from 'react-redux';
import { RefreshEvents } from '../../common/SharedEvents';
import DealModal from '../modals/DealModal';
import EditStdLoanModal from '../../loans/modals/EditStdLoanModal';
import LinkLoanWithBClientModal from '../../loans/modals/LinkLoanWithBClientModal';
import { LoadPanel } from 'devextreme-react/load-panel';
import { handleAdminInfo, handleAdminSuccess } from '../../common/SharedFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlag, faArrowCircleDown, faHourglassHalf, faFileExcel, faFileAlt } from '@fortawesome/free-solid-svg-icons';

const activeColumns = [
    { key: 'Flgd', label: 'Flag', id: 'isFlagged' },
    { key: 'Broker First Name', label: 'Broker First Name', id: 'firstName' },
    { key: 'Last Name', label: 'Last Name', id: 'lastName' },
    {
        key: 'Point of Contact',
        label: 'Point of Contact',
        id: 'pointOfContact',
    },
    { key: 'B-Client Flgd', label: 'Flag', id: 'clientIsFlagged' },
    { key: 'B-Client (Rep.)', label: 'B-Client (Rep.)', id: 'personName' },
    { key: 'Business', label: 'Business', id: 'businessName' },
    {
        key: 'Days Until Expires',
        label: 'Days Until Expires',
        id: 'referralDaysRemaining',
    },
];

const InitialExpiryBrokerClientTable = () => {
    const searchState = useSelector((state) => state.searchState);
    const brokerClientApi = new BrokerClientApi.BrokerClientApi(applicationInsights);
    const fileApi = new FileApi.FileApi(applicationInsights);
    const { dispatch } = store;

    const refreshEvents = new RefreshEvents();

    const [isLoading, set_isLoading] = React.useState(false);
    const [openDealModal, set_openDealModal] = React.useState(false);
    const [openCreatePdfModal, set_openCreatePdfModal] = React.useState(false);
    const [createPdfModalText, set_createPdfModalText] = React.useState(false);

    useEffect(() => {
        if (createPdfModalText) set_openCreatePdfModal(true);
    }, [createPdfModalText]);

    const _handleRowClick = (row) => {
        dispatch(brokerClientActions.setBrokerClientId({ brokerClientId: row.id }));
        dispatch(
            brokerClientActions.setBrokerClientOpen({
                openBrokerClientModal: true,
            })
        );
    };

    const _refreshGrid = async (e) => {
        e.preventDefault();
        await _loadBrokerClients();
    };

    const _handleDealButton = (brokerClientId) => {
        dispatch(brokerClientActions.setBrokerClientId({ brokerClientId }));
        set_openDealModal(true);
    };

    const _handleDealModalSubmit = () => {
        refreshEvents.dispatchRefreshEvent();
        set_openDealModal(false);
    };

    const _handleDealModalCancel = () => {
        set_openDealModal(false);
    };

    const _handleCreatePdfConfirm = () => {
        set_openCreatePdfModal(false);
    };

    const _loadBrokerClients = async () => {
        const response = await brokerClientApi.getExpireSoonBrokerClientGridRows();

        if (response.statusCode !== 200) return;

        const dataRows = response.result;
        dataRows.forEach((row) => {
            if (row.generatingIccaPdfUrl) {
                const ws = new WebSocket(row.generatingIccaPdfUrl);
                ws.onopen = () => {
                    handleAdminInfo('Generating PDF...');
                };

                ws.onmessage = async (event) => {
                    await _loadBrokerClients();
                    handleAdminSuccess('ICCA PDF Ready for download');
                };
            }
        });

        set_rows(dataRows);
    };

    const [rows, set_rows] = React.useState([]);
    const [sort, set_sort] = React.useState({
        column: 'name',
        direction: 'asc',
    });

    const matchFirstName = (s) =>
        s.firstName && s.firstName.toLowerCase().includes(searchState.searchText.brokerClient.toLowerCase());
    const matchLastName = (s) =>
        s.lastName && s.lastName.toLowerCase().includes(searchState.searchText.brokerClient.toLowerCase());
    const mastchPersonName = (s) =>
        s.personName && s.personName.toLowerCase().includes(searchState.searchText.brokerClient.toLowerCase());
    const matchBusinessName = (s) =>
        s.businessName && s.businessName.toLowerCase().includes(searchState.searchText.brokerClient.toLowerCase());

    const sortedRows = _.chain(rows)
        .filter(
            (s) =>
                !searchState.searchText.brokerClient ||
                matchFirstName(s) ||
                matchLastName(s) ||
                mastchPersonName(s) ||
                matchBusinessName(s)
        )
        .orderBy(sort.column, sort.direction)
        .value();

    const _downloadIcca = async (iccaDownloadToken) => {
        const response = await fileApi.downloadUrl({
            token: iccaDownloadToken,
        });

        if (response.statusCode !== 200) return;
        window.location.href = response.result.downloadUrl;
    };

    const _createIcca = async (id) => {
        try {
            set_isLoading(true);
            const response = await brokerClientApi.createBrokerAgreementPdf(id);
            set_isLoading(false);
            if (response?.statusCode === 200) {
                await _loadBrokerClients();
            } else {
                set_createPdfModalText('The system was not able to process this PDF. Please contact support.');
            }
        } catch {
            set_createPdfModalText('The system was not able to process this PDF. Please contact support.');
        } finally {
            set_isLoading(false);
        }
    };

    useEffect(() => {
        async function load() {
            await _loadBrokerClients();

            refreshEvents.addRefreshListener(_loadBrokerClients);
        }

        load();
    }, []);

    const styles = {
        rowWithRedText: {
            color: '#E00400',
        },
    };

    return (
        <Accordion title='Initial Expiry Risk, Deal Closed? (waiting on BFI)'>
            <div className='card'>
                <div className='card-section'>
                    <table>
                        <thead>
                            <tr>
                                {activeColumns.map((col) => (
                                    <SortableHeader
                                        key={col.key}
                                        column={col}
                                        sort={sort}
                                        onPress={set_sort}
                                    />
                                ))}
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRows.map((row) => {
                                return (
                                    <tr
                                        key={row.id}
                                        style={row.contractMonthsRemaining > 0 ? null : styles.rowWithRedText}
                                    >
                                        <td
                                            className='isFlagged'
                                            onClick={() => _handleRowClick(row)}
                                        >
                                            {row.isFlagged && (
                                                <i
                                                    style={{ color: 'darkred' }}
                                                    title='Broker is Flagged!'
                                                >
                                                    <FontAwesomeIcon icon={faFlag} />
                                                </i>
                                            )}
                                        </td>
                                        <td
                                            className='primary'
                                            onClick={() => _handleRowClick(row)}
                                        >
                                            {row.firstName}
                                        </td>
                                        <td
                                            className='primary'
                                            onClick={() => _handleRowClick(row)}
                                        >
                                            {row.lastName}
                                        </td>

                                        <td
                                            className='secondary'
                                            onClick={() => _handleRowClick(row)}
                                        >
                                            {row.pointOfContact}
                                        </td>
                                        <td
                                            className='secondary'
                                            onClick={() => _handleRowClick(row)}
                                        >
                                            {row.clientIsFlagged && (
                                                <i
                                                    style={{ color: 'darkred' }}
                                                    title='B-Client is Flagged!'
                                                >
                                                    <FontAwesomeIcon icon={faFlag} />
                                                </i>
                                            )}
                                        </td>
                                        <td
                                            className='secondary'
                                            onClick={() => _handleRowClick(row)}
                                        >
                                            {row.personName}
                                        </td>
                                        <td
                                            className='secondary'
                                            onClick={() => _handleRowClick(row)}
                                        >
                                            {row.businessName}
                                        </td>
                                        <td
                                            className='secondary'
                                            onClick={() => _handleRowClick(row)}
                                        >
                                            {row.referralDaysRemaining} days
                                        </td>
                                        <td className='secondary actions'>
                                            <button
                                                className='gridButton'
                                                onClick={() => _handleDealButton(row.id)}
                                            >
                                                <FontAwesomeIcon icon={faFileAlt} /> Deal Y/N
                                            </button>
                                            {row.iccaDownloadToken && !row.generatingIccaPdfUrl && (
                                                <button
                                                    className='gridButton'
                                                    onClick={() => _downloadIcca(row.iccaDownloadToken)}
                                                >
                                                    <FontAwesomeIcon icon={faArrowCircleDown} /> Download
                                                </button>
                                            )}
                                            {row.generatingIccaPdfUrl && (
                                                <span
                                                    style={{
                                                        color: 'black',
                                                        width: '90px',
                                                        textAlign: 'center',
                                                        display: 'inline-block',
                                                    }}
                                                    onClick={(e) => _refreshGrid(e)}
                                                >
                                                    <FontAwesomeIcon
                                                        title='ICCA PDF is generating...'
                                                        icon={faHourglassHalf}
                                                    />
                                                </span>
                                            )}
                                            {!row.iccaDownloadToken && !row.generatingIccaPdfUrl && row.canGenerateIccaPdf && (
                                                <button
                                                    className='gridButton red-text'
                                                    onClick={() => _createIcca(row.id)}
                                                >
                                                    <FontAwesomeIcon icon={faFileExcel} /> Create PDF
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            <DealModal
                isOpen={openDealModal}
                submitCallback={_handleDealModalSubmit}
                cancelCallback={_handleDealModalCancel}
            />
            <EditStdLoanModal />
            <LinkLoanWithBClientModal />
            <InformModal
                isOpen={openCreatePdfModal}
                confirmCallback={_handleCreatePdfConfirm}
                text={createPdfModalText}
            />
            <LoadPanel
                hideOnOutsideClick={false}
                visible={isLoading}
                message={'Generating PDF...'}
            />
        </Accordion>
    );
};

export default InitialExpiryBrokerClientTable;
